import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiX } from "react-icons/fi";
import { CreatePhonemeDocument, type PhonemeType } from "../../../generated/graphql";
import { TagsInput } from "../TagsInput";
import { PHONEME_TYPES } from "./constants";

interface AddPhonicsModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for adding custom phonemes (without audio file)
 * Audio files will be added later through VoicesModal
 */
/** phones of an Arpabet sequence, keyed so a repeated phone still gets a stable key */
function phoneOptions(sequence: string) {
  const seen = new Map<string, number>();
  return sequence
    .trim()
    .toLowerCase()
    .split("|")
    .map((phone, index) => {
      const occurrence = (seen.get(phone) ?? 0) + 1;
      seen.set(phone, occurrence);
      return { phone: phone || "\u2014", index, key: `${phone}-${occurrence}` };
    });
}

export function AddPhonicsModal({ onClose, onSuccess }: AddPhonicsModalProps) {
  const [phoneme, setPhoneme] = useState("");
  const [phonemeType, setPhonemeType] = useState<string>("consonant");
  const [tags, setTags] = useState<string[]>([]);
  const [sequence, setSequence] = useState("");
  const [targetIndex, setTargetIndex] = useState(0);

  const [createPhoneme, { loading }] = useMutation(CreatePhonemeDocument, {
    onCompleted: () => {
      toast.success("Custom phoneme added! Upload audio files through the voice modal.");
      onSuccess();
    },
    onError: (error) => {
      toast.error(`Failed to add phoneme: ${error.message}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phoneme || !phonemeType) {
      toast.error("Please fill in all required fields");
      return;
    }

    const trimmedSequence = sequence.trim().toLowerCase();
    if (!/^[a-z]{1,3}(\|[a-z]{1,3})*$/.test(trimmedSequence)) {
      toast.error("Arpabet sequence must be codes separated by | (e.g. b|ah)");
      return;
    }
    if (targetIndex >= trimmedSequence.split("|").length) {
      toast.error("Scored phone is outside the sequence");
      return;
    }

    try {
      await createPhoneme({
        variables: {
          input: {
            phoneme: phoneme.toLowerCase().trim(),
            phonemeType: phonemeType as PhonemeType,
            tags: tags.length > 0 ? tags : [],
            arpabet: { sequence: trimmedSequence, targetIndex },
          },
        },
      });
    } catch (error) {
      console.error("Add phoneme failed:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Custom Phoneme</h2>
            <p className="mt-1 text-sm text-gray-500">
              Audio files will be uploaded later through the voice modal
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Phoneme */}
            <div>
              <label htmlFor="phoneme" className="block text-sm font-medium text-gray-700">
                Phoneme <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="phoneme"
                value={phoneme}
                onChange={(e) => setPhoneme(e.target.value)}
                placeholder="e.g., th, ch, a"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">The phoneme symbol or letter combination</p>
            </div>

            {/* Phoneme Type */}
            <div>
              <label htmlFor="phonemeType" className="block text-sm font-medium text-gray-700">
                Phoneme Type <span className="text-red-500">*</span>
              </label>
              <select
                id="phonemeType"
                value={phonemeType}
                onChange={(e) => setPhonemeType(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              >
                {PHONEME_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Arpabet */}
            <div className="rounded-md border border-gray-200 p-3 space-y-3">
              <div>
                <label
                  htmlFor="arpabetSequence"
                  className="block text-sm font-medium text-gray-700"
                >
                  Arpabet sequence <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="arpabetSequence"
                  value={sequence}
                  onChange={(e) => setSequence(e.target.value)}
                  placeholder="e.g., b|ah"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  What the pronunciation scorer listens for. Add a carrier vowel so the sound is
                  audible — "b|ah" for /b/, "hh|ae" for short a.
                </p>
              </div>
              <div>
                <label
                  htmlFor="arpabetTargetIndex"
                  className="block text-sm font-medium text-gray-700"
                >
                  Scored phone <span className="text-red-500">*</span>
                </label>
                <select
                  id="arpabetTargetIndex"
                  value={targetIndex}
                  onChange={(e) => setTargetIndex(Number(e.target.value))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {phoneOptions(sequence).map((option) => (
                    <option key={option.key} value={option.index}>
                      {option.index}: {option.phone}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Only this phone is graded; the rest is the carrier.
                </p>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                Tags (optional)
              </label>
              <div className="mt-1">
                <TagsInput
                  value={tags}
                  onChange={setTags}
                  placeholder="voiced, unvoiced, etc."
                  disabled={loading}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Press Enter or comma to add tags</p>
            </div>

            {/* Info Box */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> After adding this phoneme, click on it to open the voice
                modal and upload audio files for different voice variants.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Phoneme"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
